import React, { useCallback, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Moon, Sun, Sparkles, Check, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type ThemeValue = 'light' | 'dark' | 'ema';
type ThemeOption = { value: ThemeValue; labelKey: string; icon: React.ReactNode; swatch: string[] };

const THEME_OPTIONS: ThemeOption[] = [
  { value: 'light', labelKey: 'settings.themeLight', icon: <Sun className="w-4 h-4" />, swatch: ['#ffffff', '#2563eb', '#0f172a'] },
  { value: 'dark', labelKey: 'settings.themeDark', icon: <Moon className="w-4 h-4" />, swatch: ['#0b1220', '#3b82f6', '#e2e8f0'] },
  { value: 'ema', labelKey: 'settings.themeEma', icon: <Sparkles className="w-4 h-4" />, swatch: ['#F1ECE2', '#0F766E', '#16243F'] },
];

/**
 * Reusable Appearance (theme) selector — Light / Dark / EMA.
 * ThemeContext already persists to localStorage and applies it to <html> globally.
 * This component additionally best-effort persists the choice to
 * user_notification_prefs.theme for the signed-in user (never blocks the theme change).
 */
const AppearanceCard: React.FC = () => {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);

  const chooseTheme = useCallback(async (next: ThemeValue) => {
    setTheme(next); // applies immediately + persists to localStorage via ThemeContext
    if (!user?.id) return;
    setSaving(true);
    try {
      await supabase.from('user_notification_prefs').upsert(
        { user_id: user.id, theme: next, updated_at: new Date().toISOString() },
        { onConflict: 'user_id' },
      );
    } catch (e) {
      console.error('[AppearanceCard] Could not save theme:', e);
    } finally {
      setSaving(false);
    }
  }, [user?.id, setTheme]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          {t('settings.appearance')}
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
        </CardTitle>
        <p className="text-xs text-muted-foreground">{t('settings.appearanceDesc')}</p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-3" role="radiogroup" aria-label={t('settings.theme')}>
          {THEME_OPTIONS.map((opt) => {
            const active = theme === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => chooseTheme(opt.value)}
                className={`relative flex flex-col items-center gap-2 rounded-lg border px-3 py-3 text-sm transition focus:outline-none focus:ring-2 focus:ring-ring ${active ? 'border-primary ring-1 ring-primary' : 'border-border hover:bg-muted/50'}`}
              >
                {active && <Check className="absolute right-2 top-2 h-3.5 w-3.5 text-primary" />}
                <span className="flex h-7 w-12 overflow-hidden rounded-md border border-border">
                  {opt.swatch.map((c, i) => (
                    <span key={i} className="flex-1" style={{ backgroundColor: c }} />
                  ))}
                </span>
                <span className="flex items-center gap-1.5 font-medium">{opt.icon}{t(opt.labelKey)}</span>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default AppearanceCard;
